import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Download, ExternalLink, FileText, X } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { generateMdx } from '../lib/markdown';
import type { AgentMessage } from '../lib/types';

// A document an agent wrote into the chat, open beside it. The chat keeps the
// summary; this holds the detail. It is kept as a Word file under Documents > Navaris,
// which downloads here and opens as a file to file under a document type. Marked
// `data-agent-view`, so a question asked while it is open is asked about it.
export default function DocumentViewer({
  doc, author, onClose,
}: {
  doc: AgentMessage;
  author?: string;
  onClose: () => void;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const { authFetch } = useAuth();
  const title = doc.title || 'Document';
  const filename = title.replace(/[\\/:*?"<>|]+/g, '').trim() || 'document';

  const save = (blob: Blob, name: string) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = name;
    a.click();
    URL.revokeObjectURL(url);
  };

  // The Word file where there is one; a document from before files were kept, as markdown.
  const download = async () => {
    if (doc.file_id) {
      const res = await authFetch(`/files/${doc.file_id}/raw`);
      if (res.ok) {
        save(await res.blob(), `${filename}.docx`);
        return;
      }
    }
    save(new Blob([`# ${title}\n\n${doc.content}`], { type: 'text/markdown' }), `${filename}.md`);
  };

  return (
    <div data-agent-view className="h-full flex flex-col bg-white rounded-xl border border-softgrey shadow-lg min-h-0">
      <div className="flex items-center gap-3 px-5 py-3 border-b border-softgrey shrink-0">
        <FileText className="w-5 h-5 shrink-0 text-brand-dark" />
        <div className="min-w-0 flex-1">
          <h3 className="text-base font-semibold text-graphite truncate" title={title}>{title}</h3>
          {author && <p className="text-xs text-coolslate">Written by {author}</p>}
        </div>
        {doc.file_id && (
          <Link
            to={`/files/${doc.file_id}`}
            aria-label="Open as a file"
            title="Open as a file, to file it under a document type"
            className="w-8 h-8 grid place-items-center rounded-md text-coolslate hover:text-brand-dark hover:bg-offwhite transition-colors"
          >
            <ExternalLink className="w-4 h-4" />
          </Link>
        )}
        <button
          type="button"
          onClick={download}
          aria-label={doc.file_id ? 'Download as Word' : 'Download as markdown'}
          title={doc.file_id ? 'Download as Word' : 'Download as markdown'}
          className="w-8 h-8 grid place-items-center rounded-md text-coolslate hover:text-brand-dark hover:bg-offwhite transition-colors"
        >
          <Download className="w-4 h-4" />
        </button>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close the document"
          title="Close"
          className="w-8 h-8 grid place-items-center rounded-md text-coolslate hover:text-brand-dark hover:bg-offwhite transition-colors"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
      <div className="flex-1 overflow-y-auto px-6 py-5">
        <div
          className="prose prose-sm max-w-none text-graphite"
          dangerouslySetInnerHTML={{ __html: generateMdx(doc.content) }}
        />
      </div>
    </div>
  );
}
